import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LogOut, ArrowLeft, Download, FileText, BookOpen } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export default function DashboardHeader({ hospital, simulation, readOnly, token, goBack, logout }) {
  const { t } = useTranslation();

  const exportFile = async (type) => {
    try {
      const res = await axios.get(`${API}/export/${hospital.id}/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const mime = type === "pdf" ? "application/pdf" : "text/csv";
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${hospital.name.replace(/\s+/g, "_")}_report.${type}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(`${type.toUpperCase()} export failed`);
    }
  };

  return (
    <header className="bg-white/90 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50 px-6 py-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={goBack} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
        <img src="/logo-medsim.png" alt="MedSim" className="h-[34px] rounded-md" />
        <div>
          <h1 className="text-base font-heading font-semibold text-slate-900 leading-none tracking-tight">{hospital.name}</h1>
          <p className="text-xs text-slate-400 mt-0.5">{simulation.name} &middot; {t("common.round")} {simulation.current_round}/{simulation.rounds_count}</p>
        </div>
        {readOnly && <Badge variant="outline" className="ml-2 text-[10px] border-amber-300 text-amber-600 bg-amber-50" data-testid="readonly-badge">{t("common.readOnly")}</Badge>}
      </div>
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={async () => {
          try {
            const res = await axios.get(`${API}/student-guide-pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a"); a.href = url;
            a.download = "MedBoard_Student_Guide_ENGESP.pdf";
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
          } catch { toast.error("Error downloading guide"); }
        }} data-testid="download-student-guide-btn" title="Student Guide">
          <BookOpen className="w-4 h-4 mr-1.5" />Guide
        </Button>
        <ThemeToggle />
        <LanguageSwitcher />
        {!readOnly && (
          <Button variant="ghost" size="sm" onClick={() => exportFile("pdf")} data-testid="export-pdf-btn" title={t("common.exportPDF")}>
            <FileText className="w-4 h-4" />
          </Button>
        )}
        {!readOnly && (
          <Button variant="ghost" size="sm" onClick={() => exportFile("csv")} data-testid="export-csv-btn" title={t("common.exportCSV")}>
            <Download className="w-4 h-4" />
          </Button>
        )}
        {!readOnly && <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn"><LogOut className="w-4 h-4" /></Button>}
      </div>
    </header>
  );
}
